import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import {
  standingDefaultsFor,
  useStandingDefaults,
} from '@/features/settings/api/useStandingDefaults'
import { useActiveLanguage } from '@/i18n/useLocaleTag'
import { cn } from '@/lib/cn'
import type { StandingDefault } from '@/types/domain'

/* What every new quote starts with, for one kind, read-only (N-19).
 *
 * It sits INSIDE the «Шаблоны КП» card of the same kind, above the library the
 * administrator edits, because the question the screen used to leave open is
 * "this card is empty — so what does a quote say?". The answer is wording built
 * into the product, held in code rather than in the library, so it is shown in
 * the reader's interface language (a quote is seeded in its author's; Russian
 * stands in when the reader's language has none) and offers no edit and no
 * delete: there is no endpoint behind either. The note under the heading says
 * so in words, so the rows' lack of controls is explained rather than found.
 *
 * Whether a row is built in is the server's statement (`source`), not an
 * assumption made here; an unfamiliar source still lists its rows, under the
 * same "not editable here" sentence, because `editable` is what gates a control
 * and nothing in this panel draws one. */
export function StandingDefaults({ kind }: { kind: StandingDefault['kind'] }) {
  const { t } = useTranslation()
  const language = useActiveLanguage()
  const { data, isPending, isError, refetch } = useStandingDefaults()

  const heading = (
    <p className="text-text-heading text-[12.5px] font-normal">
      {t('settings.templates.standing.title')}
    </p>
  )

  if (isPending) {
    return (
      <div className="px-[18px] pb-3" aria-busy>
        <Skeleton className="h-16 rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-1.5 px-[18px] pb-3">
        {heading}
        <p role="alert" className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('settings.templates.standing.loadFailed')}
        </p>
        <Button variant="ghost" size="sm" onClick={() => void refetch()}>
          {t('common.actions.retry')}
        </Button>
      </div>
    )
  }

  const rows = standingDefaultsFor(data, kind, language)

  return (
    <section
      aria-label={t('settings.templates.standing.title')}
      className="flex flex-col gap-1 px-[18px] pb-3"
    >
      {heading}
      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {t('settings.templates.standing.note')}
      </p>
      {rows.length > 0 ? (
        <ul className="mt-1 flex flex-col">
          {rows.map((row, index) => (
            <li
              key={`${row.language}-${row.position}`}
              className={cn(
                'text-text-body py-1.5 text-[12.5px] leading-snug font-light',
                index !== rows.length - 1 && 'border-line-hair border-b',
              )}
            >
              {row.body}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('settings.templates.standing.none')}
        </p>
      )}
    </section>
  )
}
